import React from "react";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { formatMbps, utilizationPercent } from "./bandwidthDisplay";
import { formatDuration } from "./timeControls";

/** One end of a DCI: named by its role ("Port A"), with its id as a reference. */
export interface ChartEndpoint {
  label: string;
  /** Subscription id or name, shown on hover only; `null` when the API sends none. */
  reference: string | null;
}

export interface PeakSeries {
  key: string;
  direction: "inbound" | "outbound";
  /** Present on DCI, where each port has its own pair of series. */
  endpoint?: ChartEndpoint;
  /** Highest interval average in Mbps, or `null` when the range has no reading. */
  peakMbps: number | null;
}

interface Props {
  series: PeakSeries[];
  capacityMbps: number | null;
  /** The chart's averaging interval ("5m"); every peak is one of its averages. */
  interval: string | null;
}

// Below 10 % a whole number reads as "0 %" or "3 %" for very different loads.
function formatPercent(percent: number): string {
  return `${percent < 10 ? percent.toFixed(1) : Math.round(percent)} %`;
}

const INTERVAL_UNIT_WORD: Record<string, string> = { s: "second", m: "minute", h: "hour", d: "day" };

// "5m" → "5-minute", as in "the highest 5-minute average".
function intervalAdjective(interval: string): string {
  const match = interval.match(/^(\d+)([smhd])$/);
  return match ? `${match[1]}-${INTERVAL_UNIT_WORD[match[2]]}` : interval;
}

function EndpointName({ label, reference }: ChartEndpoint) {
  if (!reference) return <>{label}</>;
  return (
    <GenericTooltip text={`${label}: ${reference}`} position="top">
      <span className="cursor-pointer hover:underline">{label}</span>
    </GenericTooltip>
  );
}

function PeakTile({ direction, endpoint, peakMbps, capacityMbps }: Omit<PeakSeries, "key"> & { capacityMbps: number | null }) {
  const percent = peakMbps === null ? null : utilizationPercent(peakMbps, capacityMbps);

  return (
    <div className="flex flex-col min-w-[140px]">
      <span className="text-xs text-semantic-text-secondary">
        Peak {endpoint && <><EndpointName {...endpoint} /> </>}{direction}
      </span>
      <span className="text-body-medium font-bold text-semantic-text-primary">
        {peakMbps === null ? "No data" : formatMbps(peakMbps)}
      </span>
      {percent !== null && capacityMbps !== null && (
        <span className="text-xs text-semantic-text-secondary">
          {formatPercent(percent)} of {formatMbps(capacityMbps)}
        </span>
      )}
    </div>
  );
}

/**
 * Peak utilization for the range the chart is showing, derived from the series
 * it already downloaded (CTX-779). The series is one mean per interval, so the
 * caption names the interval: the same link reads a lower peak at 1 hour than
 * at 5 minutes, and a bare number would hide why.
 *
 * `series` is only what the chart is showing: a series toggled off in the
 * legend takes its peak with it.
 */
export default function BandwidthPeaks({ series, capacityMbps, interval }: Props) {
  if (series.length === 0) return null;

  return (
    <div className="flex flex-wrap items-end gap-x-10 gap-y-4 mx-5 mb-2">
      {series.map(({ key, ...peak }) => (
        <PeakTile key={key} {...peak} capacityMbps={capacityMbps} />
      ))}
      {interval && (
        <div className="flex items-center gap-1 text-xs text-semantic-text-secondary">
          <span>Highest {intervalAdjective(interval)} average in the selected range</span>
          <GenericTooltip
            text={`The chart plots the mean traffic of every ${formatDuration(interval).toLowerCase()}. A peak is the highest of those means, so a shorter interval can show a higher peak for the same traffic.`}
            width="20rem"
            horizontalAlign="right"
          />
        </div>
      )}
    </div>
  );
}
